<template>
  <!-- <div class="san-instant-search fit"> -->
  <slot
    :search-client="searchClient"
    :instant-search-instance="instantSearchInstance"
  />
  <!-- </div> -->
</template>

<script>
const cache = new Set()
const warn = message => {
  if (cache.has(message)) return
  cache.add(message)
  console.warn(message)
}
const oldApiWarning = `Vue InstantSearch: You used the prop api-key or app-id.
These have been replaced by search-client.

See more info here: https://www.algolia.com/doc/api-reference/widgets/instantsearch/vue/#widget-param-search-client`
</script>

<script setup>
import { inject, ref, watch, onMounted, nextTick, onBeforeUnmount, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import instantsearch from 'instantsearch.js/es'
import { INSTANTSEARCH_FUTURE_DEFAULTS } from 'instantsearch.js/es/lib/InstantSearch'
import Adapter from 'typesense-instantsearch-adapter/src/TypesenseInstantsearchAdapter'
import { SearchResponseAdapter } from 'typesense-instantsearch-adapter/src/SearchResponseAdapter.js'
import { isEmpty } from 'lodash' // eslint-disable-line

const props = defineProps ({
  insightsClient: { type: Function, default: undefined },
  indexName: { type: String, required: true },
  compositionID: { type: String, default: undefined },
  insights: {
    default: undefined,
    validator: value => typeof value === 'undefined' || typeof value === 'boolean' || typeof value === 'object'
  },
  stalledSearchDelay: { type: Number, default: undefined },
  onStateChange: { type: Function, default: undefined },
  initialUiState: { type: Object, default: undefined },
  apiKey: {
    type: String,
    default: undefined,
    validator: value => {
      if (value) warn(oldApiWarning)
      return false
    }
  },
  appId: {
    type: String,
    default: undefined,
    validator: value => {
      if (value) warn(oldApiWarning)
      return false
    }
  },
  middlewares: { type: Array, default: null },
  future: { type: Object, default: () => ({}) },
  searchParams: { type: Object, default: () => {} },
  minQueryLength: { type: Number, default: 0 }
})

const instantSearchProps = inject('instantSearchProps') // eslint-disable-line
const searchParameters = inject('searchParameters') // eslint-disable-line

const $route = useRoute()
const $router = useRouter()

class CustomSearchResponseAdapter extends SearchResponseAdapter {
  // Override _adaptRenderingContent adding original request items to the adapted result.
  _adaptRenderingContent (typesenseFacetCounts) {
    const adaptedResult = super._adaptFacetStats(typesenseFacetCounts)
    Object.entries(adaptedResult).reduce((acc, [key, val]) => {
      val.values = typesenseFacetCounts.find(e => e.field_name === key).counts
      acc[key] = val
      return acc
    }, {})
    return adaptedResult
  }
}

class CustomAdapter extends Adapter { // eslint-disable-line
  // Override searchTypesenseAndAdapt method to use CustomSearchResponseAdapter
  async searchTypesenseAndAdapt (instantsearchRequests) {
    let typesenseResponse
    try {
      typesenseResponse = await this._adaptAndPerformTypesenseRequest(instantsearchRequests)

      const adaptedResponses = typesenseResponse.results.map((typesenseResult, index) => {
        this._validateTypesenseResult(typesenseResult)
        const responseAdapter = new CustomSearchResponseAdapter(
          typesenseResult,
          instantsearchRequests[index],
          this.configuration,
          typesenseResponse.results,
          typesenseResponse
        )
        const adaptedResponse = responseAdapter.adapt()

        return adaptedResponse
      })

      // console.log('%cadaptedResponses', 'color:lime', adaptedResponses)

      const res = {
        results: adaptedResponses
      }
      return res
    } catch (error) {
      console.error(error)
      throw error
    }
  }
}

const typesenseAdapter = new Adapter({
// const typesenseAdapter = new Adapter({
  server: {
    apiKey: import.meta.env.TYPESENSE_API_KEY,
    nodes: [
      {
        host: import.meta.env.TYPESENSE_HOST,
        port: import.meta.env.TYPESENSE_PORT,
        protocol: import.meta.env.TYPESENSE_PROTOCOL
      }
    ],
    cacheSearchResultsForSeconds: 2 * 60
  },
  additionalSearchParameters: {
    // q: '*',
    ...props.searchParams
  }
})

const searchClient = {
  ...typesenseAdapter.searchClient,
  search: async req => {
    const isEmptyQuery = req.every(({ params: { query } }) => !query?.trim())
    if (isEmptyQuery) {
      // req.params.query = '*'
      // req.page = -1
    }
    // const res = isEmptyQuery
    //   ? await Promise.resolve({
    //       results: req.map(() => ({
    //         facets: {},
    //         facetStats: {},
    //         hits: [],
    //         hitsPerPage: 0,
    //         nbHits: 0,
    //         nbPages: 0,
    //         page: 0,
    //         processingTimeMS: 0,
    //         query: '',
    //         renderingContent: {},
    //         exhaustiveNbHits: false,
    //         params: ''
    //       }))
    //     })
    //   : await typesenseAdapter.searchClient.search(req)
    for (const r of req) {
      if (!r.params.query) {
        // r.params.query = '*'
        // r.params.page = -1
        // r.params.per_page = 0
        // r.params.facet_by = []
        // r.params.facet_query = []
        // r.params.facet_stats = []
        // r.params.sort_by = undefined
        // r.params.filter_by = undefined
        // r.params.group_by = undefined
        // r.params.group_limit = undefined
        // r.params.include_fields = undefined
        // r.params.exclude_fields = undefined
        // r.params.highlight_full_fields = undefined
        // r.params.highlight_affix_num_tokens = undefined
        // r.params.highlight_start_tag = undefined
        // r.params.highlight_end_tag = undefined
        // r.params.snippet_threshold = undefined
        // r.params.drop_tokens_threshold = undefined
        // r.params.typo_tokens_threshold = undefined
        // r.params.pinned_hits = undefined
        // r.params.hidden_hits = undefined
        // r.params.limit_hits = undefined
        // r.params.per_page = undefined
        // r.params.group_limit = undefined
        // r.params.group_by = undefined
        // r.params.group_by = undefined

      }
    }
    const res = await typesenseAdapter.searchClient.search(req)
    console.log('%csearchClient', 'color:orange', req)
    return res
  }
}

const routing = {
  router: {
    read: () => $route.query,
    write: query => $router.replace({ query }),
    createURL: () => $route.fullPath,
    onUpdate: cb => {
      routing.router._onPopState = () => cb(routing.router.read())
      window.addEventListener('popstate', routing.router._onPopState)
    },
    dispose: () => {
      window.removeEventListener('popstate', () => {})
    }
  },
  stateMapping: {
    stateToRoute: uiState => {
      // console.log('%cstateToRoute', 'color:orange', uiState)
      return {
        q: uiState[props.indexName].query
      }
    },
    routeToState: routeState => {
      routeState = !routeState || isEmpty(routeState)
        ? { [props.indexName]: { query: undefined } }
        : {
            [props.indexName]: {
              query: routeState.q
              // refinementList: {
              //   brand: routeState.brands && routeState.brands.split('~')
              // },
              // page: routeState.page // causing error in hits component
            }
          }
      // const state = {
      //   [props.indexName]: {
      //     configure: {
      //       hitsPerPage: !routeState.q ? 0 : 20
      //     }
      //   }
      // }
      // routeState[props.indexName].configure.hitsPerPage = 20
      // searchParameters.hitsPerPage = 20

      // console.log('%crouteToState', 'color:lime', routeState)
      return routeState
    }
  }
}

const onStateChange = ({ uiState, setUiState }) => {
  const { [props.indexName]: { query } } = uiState

  // console.log('%conStateChange', 'color:lime', uiState)
  if (!query || query.length < props.minQueryLength) {
    setUiState({ [props.indexName]: { query: '' } }) // no search if less than 2 character
  } else {
    setUiState(uiState)
  }
  if (props.onStateChange) props.onStateChange({ uiState, setUiState })
}

const instantSearchInstance = ref(null)
const createInstantSearchInstance = () => instantsearch({
  searchClient,
  insightsClient: props.insightsClient,
  insights: props.insights,
  indexName: props.indexName,
  compositionID: props.compositionID,
  routing,
  stalledSearchDelay: props.stalledSearchDelay,
  onStateChange,
  // initialUiState: props.initialUiState,
  future: {
    preserveSharedStateOnUnmount: true,
    persistHierarchicalRootCount: true,
    ...props.future
  }
})
instantSearchInstance.value = createInstantSearchInstance()

watch(() => searchClient.value, val => {
  warn(false, 'The `search-client` prop of `<san-instant-search>` changed between renders, which may cause more search requests than necessary. If this is an unwanted behavior, please provide a stable reference: https://www.algolia.com/doc/api-reference/widgets/instantsearch/vue/#widget-param-search-client')
  instantSearchInstance.value.helper.setClient(val).search()
  // instantSearchInstance.value.updateConfiguration(options) // Moguće da ovo radi
})

watch(() => props.indexName, val => {
  instantSearchInstance.value.helper.setIndex(val || '').search()
})

watch(() => props.compositionID, val => {
  instantSearchInstance.value.helper
    .setIndex(val || '')
    .search()
})

watch(() => props.stalledSearchDelay, val => {
  // private InstantSearch.js API:
  instantSearchInstance.value._stalledSearchDelay = val
})

watch(() => props.routing, () => {
  throw new Error(
    'routing configuration can not be changed dynamically at this point.'
    + '\n\n'
    + 'Please open a new issue: https://github.com/algolia/instantsearch/discussions/new?category=ideas&labels=triage%2cLibrary%3A+Vue+InstantSearch&title=Feature%20request%3A%20dynamic%20props'
  )
})

watch(() => props.onStateChange, () => {
  throw new Error(
    'onStateChange configuration can not be changed dynamically at this point.'
    + '\n\n'
    + 'Please open a new issue: https://github.com/algolia/instantsearch/discussions/new?category=ideas&labels=triage%2cLibrary%3A+Vue+InstantSearch&title=Feature%20request%3A%20dynamic%20props'
  )
})

watch(() => props.middlewares, (next, prev) => {
  (prev || [])
    .filter(middleware => (next || []).indexOf(middleware) === -1)
    .forEach(middlewareToRemove => {
      this.instantSearchInstance.unuse(middlewareToRemove)
    });

  (next || [])
    .filter(middleware => (prev || []).indexOf(middleware) === -1)
    .forEach(middlewareToAdd => {
      this.instantSearchInstance.use(middlewareToAdd)
    })
}, { immediate: true })

watch(() => props.future, val => {
  instantSearchInstance.value.future = Object.assign(INSTANTSEARCH_FUTURE_DEFAULTS, val)
})

onMounted(async () => {
  await nextTick()
  if (!instantSearchInstance.value.started) {
    instantSearchInstance.value.start()
  }
})
onBeforeUnmount(() => {
  if (instantSearchInstance.value.started) instantSearchInstance.value.dispose()

  // a hydrated instance will no longer be hydrated once disposed, and starts from scratch
  instantSearchInstance.value.__initialSearchResults = undefined
})

provide('$_ais_instantSearchInstance', instantSearchInstance)
</script>
